import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone'

import { AppDialog } from '../../../components'
import CustomNumberField from '../Users/profile/CustomNumberField'
import AddCustomer from '../Customers/AddCustomer'
import SelectStateItem, {
  StateOption,
  getState,
} from '../Customers/SelectStateItem'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import {
  formatPartyAutocompleteLabel,
  formatPartyOptionSecondaryLabel,
  formatPartySelectedLabel,
  formatAmountTwoDecimals,
  getDisplayDateTime,
  getFormattedDateTimeIST,
} from '../../../utils'
import { fetchItemList } from '../../../services/items/items'
import {
  CustomerV3,
  fetchCustomerById,
  fetchCustomersV3,
} from '../../../services/customers'
import { Customer, Item } from '../../../services'
import {
  createSaleInvoice,
  CreateSaleInvoiceRequest,
  fetchNextSaleInvoiceNo,
  previewSaleInvoice,
  SaleInvoicePreviewResponse,
} from '../../../services/saleInvoices'

const ADD_NEW_PARTY = 'New Party'
const ADD_OTHER_CHARGE = 'Add Other Charge'
const OTHER_CHARGE_OPTION_ID = -1
const SALE_INVOICE_GST_RATE_KEY = 'sale_invoice_gst_rate'

type SaleGstType = 'CGST_SGST' | 'IGST' | 'NONE'

function loadCachedGstRate(): string {
  try {
    if (localStorage.getItem(SALE_INVOICE_GST_RATE_KEY) !== null) {
      return localStorage.getItem(SALE_INVOICE_GST_RATE_KEY) ?? ''
    }
  } catch {
    // ignore
  }
  return '18'
}

function persistGstRate(value: string) {
  try {
    localStorage.setItem(SALE_INVOICE_GST_RATE_KEY, value)
  } catch {
    // ignore
  }
}

type PartyOption = {
  id: number
  label: string
  name?: string
  state?: string
  stateCode?: string
  mobile?: string
  address?: string
  address2?: string
  place?: string
  pincode?: string
  gstin?: string
}

type LineDraft = {
  key: string
  itemId: number
  name: string
  quantity: number
  rate: number
  amount: number
  isOtherCharge?: boolean
}

export interface CreateSaleInvoiceDialogProps {
  /** Pre-select party after returning from Add Party dialog */
  initialParty?: Customer | null
  onClose: (created?: boolean) => void
}

function toPartyOption(customer: Customer): PartyOption {
  return {
    id: customer.id,
    label: formatPartySelectedLabel(customer.name, customer.state),
    name: customer.name,
    state: customer.state,
    stateCode: customer.stateCode,
    mobile: customer.mobile,
    address: customer.address,
    address2: customer.address2,
    place: customer.place,
    pincode: customer.pincode,
    gstin: customer.gstin,
  }
}

function formatAddressBlock(addr?: {
  name?: string
  add1?: string
  add2?: string
  place?: string
  state?: string
  stateCode?: string
  pincode?: string
  fullAddress?: string
}) {
  if (!addr) return ''
  if (addr.fullAddress) return addr.fullAddress
  return [addr.add1, addr.add2, addr.place, addr.state, addr.pincode]
    .filter((part) => part != null && String(part).trim() !== '')
    .join(', ')
}

export const CreateSaleInvoiceDialog: FC<CreateSaleInvoiceDialogProps> = ({
  initialParty,
  onClose,
}) => {
  const dispatchAction = useAppDispatch()
  const { data: userData } = useAppSelector((state) => state.userData)

  const [step, setStep] = useState<'edit' | 'preview'>('edit')
  const [partyList, setPartyList] = useState<CustomerV3[]>([])
  const [party, setParty] = useState<PartyOption | null>(
    initialParty ? toPartyOption(initialParty) : null
  )
  const [invoiceDate, setInvoiceDate] = useState<Date>(new Date())
  const [invoiceNo, setInvoiceNo] = useState<string>('')
  const [discount, setDiscount] = useState<number>(0)
  const [gstRate, setGstRate] = useState<string>(() => loadCachedGstRate())
  const [shipAdd1, setShipAdd1] = useState('')
  const [shipAdd2, setShipAdd2] = useState('')
  const [shipPlace, setShipPlace] = useState('')
  const [shipPincode, setShipPincode] = useState('')
  const [shipStateOption, setShipStateOption] = useState<StateOption | null>(
    () => (initialParty?.stateCode ? getState(initialParty.stateCode) : null)
  )
  const [gstType, setGstType] = useState<SaleGstType>('NONE')
  const [lines, setLines] = useState<LineDraft[]>([])
  const [itemOptions, setItemOptions] = useState<Item[]>([])
  const [selectedItem, setSelectedItem] = useState<Item | null>(null)
  const [otherChargeName, setOtherChargeName] = useState('')
  const [quantity, setQuantity] = useState<string>('')
  const [rate, setRate] = useState<string>('')
  const [preview, setPreview] = useState<SaleInvoicePreviewResponse | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [creating, setCreating] = useState(false)
  const [showAddParty, setShowAddParty] = useState(false)
  const otherChargeNameRef = useRef<HTMLInputElement | null>(null)
  const quantityRef = useRef<HTMLInputElement | null>(null)

  const isGstSeries = Boolean(userData?.store?.gstin)
  const storeStateCode = userData?.store?.stateCode

  /** Derive GST type from store vs ship states only. Manual dropdown changes never touch ship state. */
  const resolveDefaultGstType = useCallback(
    (placeStateCode?: string | null): SaleGstType => {
      const storeCode = storeStateCode?.trim()
      const placeCode = placeStateCode?.trim()
      if (!storeCode || !placeCode) {
        return 'NONE'
      }
      if (storeCode.toLowerCase() === placeCode.toLowerCase()) {
        return 'CGST_SGST'
      }
      return 'IGST'
    },
    [storeStateCode]
  )

  const applyShipFromCustomer = useCallback((customer: Customer | PartyOption | null) => {
    if (!customer) {
      setShipAdd1('')
      setShipAdd2('')
      setShipPlace('')
      setShipPincode('')
      setShipStateOption(null)
      return
    }
    setShipAdd1(customer.address || '')
    setShipAdd2(customer.address2 || '')
    setShipPlace(customer.place || '')
    setShipPincode(customer.pincode || '')
    if (customer.stateCode) {
      setShipStateOption(getState(customer.stateCode))
    } else {
      setShipStateOption(null)
    }
  }, [])

  const loadPartyListV3 = useCallback(() => {
    fetchCustomersV3()
      .then((res: any) => {
        const payload = res?.data?.data ?? res?.data
        const list = Array.isArray(payload) ? payload : []
        setPartyList(list as CustomerV3[])
      })
      .catch(() => {
        dispatchAction(
          showAlertAction({
            message: 'Failed to load parties',
            severity: 'error',
          })
        )
      })
  }, [dispatchAction])

  const loadShipFromPartyId = useCallback(
    (partyId: number, fallback?: PartyOption | null) => {
      if (partyId <= 0) {
        applyShipFromCustomer(null)
        return
      }
      applyShipFromCustomer(fallback || null)
      fetchCustomerById(partyId)
        .then((response: any) => {
          const payload = response?.data?.data ?? response?.data
          if (payload?.id) {
            const full = payload as Customer
            applyShipFromCustomer(full)
            setParty(toPartyOption(full))
          }
        })
        .catch(() => {
          /* keep fallback */
        })
    },
    [applyShipFromCustomer]
  )

  // Sync GST type from states when ship/store state changes. Dropdown override is independent
  // of address fields (changing GST never mutates ship state).
  useEffect(() => {
    setGstType(resolveDefaultGstType(shipStateOption?.code))
  }, [shipStateOption?.code, storeStateCode, resolveDefaultGstType])

  const refreshNextInvoiceNo = useCallback(
    (date: Date) => {
      fetchNextSaleInvoiceNo(getFormattedDateTimeIST(date), isGstSeries)
        .then((response) => {
          if (response?.success && response.data != null) {
            setInvoiceNo(String(response.data))
          }
        })
        .catch(() => {
          /* keep existing value */
        })
    },
    [isGstSeries]
  )

  useEffect(() => {
    loadPartyListV3()
    fetchItemList()
      .then((res: any) => {
        const payload = res?.data?.data ?? res?.data
        const items: Item[] = payload?.items ?? []
        setItemOptions(Array.isArray(items) ? items : [])
      })
      .catch(() => {
        dispatchAction(
          showAlertAction({
            message: 'Failed to load items',
            severity: 'error',
          })
        )
      })
  }, [dispatchAction, loadPartyListV3])

  useEffect(() => {
    refreshNextInvoiceNo(invoiceDate)
  }, [refreshNextInvoiceNo])

  useEffect(() => {
    if (initialParty) {
      const option = toPartyOption(initialParty)
      setParty(option)
      applyShipFromCustomer(initialParty)
    }
  }, [initialParty, applyShipFromCustomer])

  const partyOptions: PartyOption[] = useMemo(() => {
    const list = (partyList || []).map((customer) => ({
      id: customer.id,
      label: formatPartyAutocompleteLabel(
        customer.name,
        undefined,
        customer.address
      ),
      name: customer.name,
      address: customer.address,
    }))
    return [{ id: 0, label: ADD_NEW_PARTY }, ...list]
  }, [partyList])

  const availableItems = useMemo(() => {
    const used = new Set(
      lines.filter((l) => !l.isOtherCharge).map((l) => l.itemId)
    )
    return itemOptions.filter((item) => item.id != null && !used.has(item.id))
  }, [itemOptions, lines])

  const itemPickOptions = useMemo(() => {
    const otherChargeOption = {
      id: OTHER_CHARGE_OPTION_ID,
      itemName: ADD_OTHER_CHARGE,
    } as Item
    return [otherChargeOption, ...availableItems]
  }, [availableItems])

  const isOtherChargeSelected =
    selectedItem?.id === OTHER_CHARGE_OPTION_ID ||
    selectedItem?.itemName === ADD_OTHER_CHARGE

  const onPartyChange = (_e, newValue: PartyOption | null) => {
    if (!newValue) {
      setParty(null)
      applyShipFromCustomer(null)
      return
    }
    if (newValue.label === ADD_NEW_PARTY) {
      setShowAddParty(true)
      return
    }
    // Label may only have name until by-id returns state for Ship To.
    const selected = {
      ...newValue,
      label: formatPartySelectedLabel(newValue.name, newValue.state),
    }
    setParty(selected)
    loadShipFromPartyId(selected.id, selected)
  }

  const buildRequest = (): CreateSaleInvoiceRequest | null => {
    if (!party || party.id <= 0) {
      dispatchAction(
        showAlertAction({ message: 'Please select a party', severity: 'error' })
      )
      return null
    }
    if (lines.length === 0) {
      dispatchAction(
        showAlertAction({
          message: 'Please add at least one item',
          severity: 'error',
        })
      )
      return null
    }
    const rateNum = Number(gstRate)
    if (gstType !== 'NONE' && (gstRate.trim() === '' || Number.isNaN(rateNum) || rateNum < 0)) {
      dispatchAction(
        showAlertAction({
          message: 'Enter a valid GST rate',
          severity: 'error',
        })
      )
      return null
    }
    return {
      buyerCustomerId: party.id,
      invoiceDate: getFormattedDateTimeIST(invoiceDate),
      invoiceNo: invoiceNo?.trim() || undefined,
      discount: discount || 0,
      gstType,
      gstRate: gstType === 'NONE' ? 0 : rateNum,
      shipAdd1: shipAdd1 || undefined,
      shipAdd2: shipAdd2 || undefined,
      shipPlace: shipPlace || undefined,
      shipState: shipStateOption?.label || undefined,
      shipStateCode: shipStateOption?.code || undefined,
      shipPincode: shipPincode || undefined,
      entries: lines.map((line) =>
        line.isOtherCharge
          ? {
              isOtherCharge: true,
              itemName: line.name,
              quantity: line.quantity,
              rate: line.rate,
            }
          : {
              itemId: line.itemId,
              quantity: line.quantity,
              rate: line.rate,
            }
      ),
    }
  }

  const handleAddLine = () => {
    if (!selectedItem || selectedItem.id == null) {
      dispatchAction(
        showAlertAction({ message: 'Select an item', severity: 'error' })
      )
      return
    }
    const qty = Number(quantity)
    const itemRate = Number(rate)
    if (!qty || qty <= 0 || Number.isNaN(qty)) {
      dispatchAction(
        showAlertAction({
          message: 'Quantity must be greater than 0',
          severity: 'error',
        })
      )
      return
    }
    if (!itemRate || itemRate <= 0 || Number.isNaN(itemRate)) {
      dispatchAction(
        showAlertAction({
          message: 'Rate must be greater than 0',
          severity: 'error',
        })
      )
      return
    }
    if (isOtherChargeSelected) {
      const chargeName = otherChargeName.trim()
      if (!chargeName) {
        dispatchAction(
          showAlertAction({
            message: 'Enter a charge name',
            severity: 'error',
          })
        )
        return
      }
      setLines((prev) => [
        ...prev,
        {
          key: `other-${Date.now()}-${prev.length}`,
          itemId: 0,
          name: chargeName,
          quantity: qty,
          rate: itemRate,
          amount: Math.round(qty * itemRate * 100) / 100,
          isOtherCharge: true,
        },
      ])
    } else {
      setLines((prev) => [
        ...prev,
        {
          key: `item-${selectedItem.id}`,
          itemId: selectedItem.id!,
          name: selectedItem.itemName,
          quantity: qty,
          rate: itemRate,
          amount: Math.round(qty * itemRate * 100) / 100,
        },
      ])
    }
    setSelectedItem(null)
    setOtherChargeName('')
    setQuantity('')
    setRate('')
  }

  const handleNext = async () => {
    const request = buildRequest()
    if (!request) return
    setLoadingPreview(true)
    try {
      const response = await previewSaleInvoice(request)
      if (response?.success && response.data) {
        setPreview(response.data)
        setStep('preview')
      } else {
        dispatchAction(
          showAlertAction({
            message: response?.message || 'Preview failed',
            severity: 'error',
          })
        )
      }
    } catch {
      dispatchAction(
        showAlertAction({ message: 'Preview failed', severity: 'error' })
      )
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleCreate = async () => {
    const request = buildRequest()
    if (!request) return
    setCreating(true)
    try {
      const response = await createSaleInvoice(request)
      if (response?.success && response.data) {
        if (response.data.pdfLink) {
          window.open(response.data.pdfLink, '_blank')
        }
        dispatchAction(
          showAlertAction({
            message: 'Sale invoice created',
            severity: 'success',
          })
        )
        onClose(true)
      } else {
        dispatchAction(
          showAlertAction({
            message: response?.message || 'Failed to create sale invoice',
            severity: 'error',
          })
        )
      }
    } catch {
      dispatchAction(
        showAlertAction({
          message: 'Failed to create sale invoice',
          severity: 'error',
        })
      )
    } finally {
      setCreating(false)
    }
  }

  const footerActionsSx = {
    flexShrink: 0,
    bgcolor: 'background.paper',
    pt: 1.5,
    pb: 0.5,
    mt: 'auto',
    borderTop: 1,
    borderColor: 'divider',
    display: 'flex',
    alignItems: 'center',
  }
  const editFooterActionsSx = {
    ...footerActionsSx,
    justifyContent: 'space-between',
    gap: 1,
  }
  const previewFooterActionsSx = {
    ...footerActionsSx,
    justifyContent: 'flex-end',
    gap: 1,
  }
  // Local full-height layout only (does not change shared AppDialog).
  // 90vh paper minus title bar + content padding.
  const dialogBodySx = {
    display: 'flex',
    flexDirection: 'column' as const,
    height: 'calc(90vh - 96px)',
    minHeight: 0,
    overflow: 'hidden',
  }
  const dialogScrollSx = {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    // Grid spacing uses negative margin; pad so first-row field labels are not clipped.
    pt: 1,
  }

  if (showAddParty) {
    return (
      <AddCustomer
        onClose={(customer) => {
          setShowAddParty(false)
          if (customer) {
            loadPartyListV3()
            const option = toPartyOption(customer)
            setParty(option)
            applyShipFromCustomer(customer)
          }
        }}
      />
    )
  }

  return (
    <AppDialog
      id="create-sale-invoice"
      title={step === 'edit' ? 'Create Sale Invoice' : 'Preview Sale Invoice'}
      onClose={() => onClose(false)}
      close={false}
      maxWidth="lg"
      fixHeight={true}
      dialogActions={false}
    >
      {step === 'edit' ? (
        <Box sx={dialogBodySx}>
          <Box sx={dialogScrollSx}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <Autocomplete
                  size="small"
                  options={partyOptions}
                  value={party}
                  onChange={onPartyChange}
                  isOptionEqualToValue={(option, value) =>
                    option?.id === value?.id
                  }
                  getOptionLabel={(option) => option?.label ?? ''}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Party"
                      required
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => {
                    if (option.label === ADD_NEW_PARTY) {
                      return (
                        <Typography
                          {...props}
                          noWrap
                          style={{
                            color: 'green',
                            height: 40,
                            marginRight: 10,
                          }}
                        >
                          {option.label}
                        </Typography>
                      )
                    }
                    const secondary = formatPartyOptionSecondaryLabel(
                      undefined,
                      option.address
                    )
                    return (
                      <Box
                        component="li"
                        {...props}
                        sx={{
                          display: 'flex !important',
                          flexDirection: 'column',
                          alignItems: 'flex-start !important',
                          py: 1,
                        }}
                      >
                        <Typography noWrap sx={{ width: '100%' }}>
                          {formatPartySelectedLabel(option.name, option.state)}
                        </Typography>
                        {secondary ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ width: '100%' }}
                          >
                            {secondary}
                          </Typography>
                        ) : null}
                      </Box>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  type="date"
                  label="Invoice Date"
                  InputLabelProps={{ shrink: true }}
                  value={invoiceDate.toISOString().slice(0, 10)}
                  onChange={(e) => {
                    const d = e.target.value
                      ? new Date(e.target.value)
                      : new Date()
                    setInvoiceDate(d)
                    refreshNextInvoiceNo(d)
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  fullWidth
                  label="Invoice No"
                  value={invoiceNo}
                  onChange={(e) => setInvoiceNo(e.target.value)}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 2.5, mb: 1 }}>
              Ship To
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Address 1"
                  value={shipAdd1}
                  onChange={(e) => setShipAdd1(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label="Address 2"
                  value={shipAdd2}
                  onChange={(e) => setShipAdd2(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Place"
                  value={shipPlace}
                  onChange={(e) => setShipPlace(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <SelectStateItem
                  selectedStateOption={shipStateOption || undefined}
                  onChange={(option) => setShipStateOption(option)}
                  label="State"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  size="small"
                  fullWidth
                  label="Pincode"
                  value={shipPincode}
                  onChange={(e) => setShipPincode(e.target.value)}
                  inputProps={{ maxLength: 6 }}
                />
              </Grid>
            </Grid>

            <Box mt={2}>
              <Grid container spacing={1.5} alignItems="flex-start">
                <Grid item xs={12} md={isOtherChargeSelected ? 3 : 5}>
                  <Autocomplete
                    size="small"
                    options={itemPickOptions}
                    value={selectedItem}
                    onChange={(_e, value) => {
                      setSelectedItem(value)
                      setOtherChargeName('')
                      setRate('')
                      if (!value) return
                      const isOtherCharge =
                        value.id === OTHER_CHARGE_OPTION_ID ||
                        value.itemName === ADD_OTHER_CHARGE
                      setTimeout(() => {
                        if (isOtherCharge) {
                          otherChargeNameRef.current?.focus()
                        } else {
                          quantityRef.current?.focus()
                        }
                      }, 0)
                    }}
                    getOptionLabel={(option) => option?.itemName ?? ''}
                    isOptionEqualToValue={(a, b) => a?.id === b?.id}
                    renderOption={(props, option) => {
                      if (option.itemName === ADD_OTHER_CHARGE) {
                        return (
                          <Typography
                            {...props}
                            noWrap
                            style={{
                              color: 'green',
                              height: 40,
                              marginRight: 10,
                            }}
                          >
                            {option.itemName}
                          </Typography>
                        )
                      }
                      return (
                        <li {...props} key={option.id}>
                          {option.itemName}
                        </li>
                      )
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Item" size="small" />
                    )}
                  />
                </Grid>
                {isOtherChargeSelected && (
                  <Grid item xs={12} md={2}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Charge name"
                      value={otherChargeName}
                      onChange={(e) => setOtherChargeName(e.target.value)}
                      inputRef={otherChargeNameRef}
                    />
                  </Grid>
                )}
                <Grid item xs={4} md={2}>
                  <CustomNumberField
                    name="quantity"
                    maxLength={10}
                    size="small"
                    fullWidth
                    label="Qty"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    inputRef={quantityRef}
                  />
                  {selectedItem &&
                    !isOtherChargeSelected &&
                    selectedItem.availableQuantity != null && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        sx={{ mt: 0.5 }}
                      >
                        Available: {selectedItem.availableQuantity}
                      </Typography>
                    )}
                </Grid>
                <Grid item xs={4} md={2}>
                  <CustomNumberField
                    name="rate"
                    maxLength={12}
                    size="small"
                    fullWidth
                    label="Rate"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </Grid>
                <Grid item xs={4} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddTwoToneIcon />}
                    onClick={handleAddLine}
                    sx={{ height: 40 }}
                  >
                    ADD
                  </Button>
                </Grid>
              </Grid>
            </Box>

            <Table size="small" sx={{ mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => (
                  <TableRow key={line.key}>
                    <TableCell>{line.name}</TableCell>
                    <TableCell align="right">{line.quantity}</TableCell>
                    <TableCell align="right">
                      {formatAmountTwoDecimals(line.rate)}
                    </TableCell>
                    <TableCell align="right">
                      {formatAmountTwoDecimals(line.amount)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setLines((prev) =>
                            prev.filter((l) => l.key !== line.key)
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {lines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" variant="body2">
                        No items added yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

          </Box>

          <Box sx={editFooterActionsSx}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box sx={{ maxWidth: 160, width: '100%' }}>
                <CustomNumberField
                  name="discount"
                  maxLength={12}
                  size="small"
                  fullWidth
                  label="Discount"
                  value={discount || ''}
                  onChange={(e) => setDiscount(Number(e.target.value) || 0)}
                />
              </Box>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="sale-gst-type-label">GST</InputLabel>
                <Select
                  labelId="sale-gst-type-label"
                  label="GST"
                  value={gstType}
                  onChange={(e) => setGstType(e.target.value as SaleGstType)}
                >
                  <MenuItem value="CGST_SGST">SGST + CGST</MenuItem>
                  <MenuItem value="IGST">IGST</MenuItem>
                  <MenuItem value="NONE">NO GST</MenuItem>
                </Select>
              </FormControl>
              {gstType !== 'NONE' && (
                <Box sx={{ maxWidth: 120, width: '100%' }}>
                  <CustomNumberField
                    name="gstRate"
                    maxLength={6}
                    size="small"
                    fullWidth
                    label="GST Rate"
                    value={gstRate}
                    onChange={(e) => {
                      const next = e.target.value
                      setGstRate(next)
                      persistGstRate(next)
                    }}
                  />
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button variant="outlined" onClick={() => onClose(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loadingPreview}
              >
                {loadingPreview ? 'Please wait...' : 'NEXT'}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={dialogBodySx}>
          <Box sx={dialogScrollSx}>
            {preview && (
              <>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Invoice Date
                    </Typography>
                    <Typography variant="body2">
                      {getDisplayDateTime(invoiceDate) ||
                        getDisplayDateTime(preview.invoiceDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Invoice No
                    </Typography>
                    <Typography variant="body2">
                      {preview.invoiceNo || invoiceNo}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Bill To
                    </Typography>
                    <Typography variant="body2">
                      {preview.billTo?.name || preview.buyer.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatAddressBlock(preview.billTo) ||
                        preview.buyer.address}
                    </Typography>
                    {(preview.billTo?.state || preview.buyer.state) && (
                      <Typography variant="body2" color="text.secondary">
                        {[
                          preview.billTo?.state || preview.buyer.state,
                          preview.billTo?.stateCode || preview.buyer.stateCode,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                    {preview.buyer.gstin && (
                      <Typography variant="body2" color="text.secondary">
                        GSTIN: {preview.buyer.gstin}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Ship To
                    </Typography>
                    <Typography variant="body2">
                      {preview.shipTo?.name || preview.buyer.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatAddressBlock(preview.shipTo)}
                    </Typography>
                    {preview.shipTo?.state && (
                      <Typography variant="body2" color="text.secondary">
                        {[preview.shipTo.state, preview.shipTo.stateCode]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Item</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Rate</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.entries.map((entry) => (
                      <TableRow key={entry.itemId}>
                        <TableCell>{entry.name}</TableCell>
                        <TableCell align="right">{entry.quantity}</TableCell>
                        <TableCell align="right">
                          {formatAmountTwoDecimals(entry.rate)}
                        </TableCell>
                        <TableCell align="right">
                          {formatAmountTwoDecimals(entry.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Box mt={2} maxWidth={320} ml="auto">
                  <BreakupRow label="Total" value={preview.breakup.total} />
                  <BreakupRow
                    label="Discount"
                    value={preview.breakup.discount}
                  />
                  <BreakupRow label="Taxable" value={preview.breakup.taxable} />
                  {preview.breakup.cgst > 0 && (
                    <BreakupRow
                      label={`CGST (${preview.breakup.cgstRate}%)`}
                      value={preview.breakup.cgst}
                    />
                  )}
                  {preview.breakup.sgst > 0 && (
                    <BreakupRow
                      label={`SGST (${preview.breakup.sgstRate}%)`}
                      value={preview.breakup.sgst}
                    />
                  )}
                  {preview.breakup.igst > 0 && (
                    <BreakupRow
                      label={`IGST (${preview.breakup.igstRate}%)`}
                      value={preview.breakup.igst}
                    />
                  )}
                  <BreakupRow
                    label="Invoice Total"
                    value={preview.breakup.invoiceTotal}
                    bold
                  />
                </Box>
              </>
            )}
          </Box>
          <Box sx={previewFooterActionsSx}>
            <Button variant="outlined" onClick={() => setStep('edit')}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating}
            >
              {creating ? 'Creating...' : 'Create Invoice'}
            </Button>
          </Box>
        </Box>
      )}
    </AppDialog>
  )
}

const BreakupRow: FC<{ label: string; value: number; bold?: boolean }> = ({
  label,
  value,
  bold,
}) => (
  <Box display="flex" justifyContent="space-between" py={0.25}>
    <Typography variant="body2" fontWeight={bold ? 700 : 400}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={bold ? 700 : 400}>
      {formatAmountTwoDecimals(value)}
    </Typography>
  </Box>
)

export default CreateSaleInvoiceDialog
